import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';

const HelpCenter: React.FC = () => {
  const [openSection, setOpenSection] = useState<string | null>('getting-started');

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  const faqs = {
    'getting-started': [
      {
        question: "How do I start using RealEstart?",
        answer: "Getting started is easy! Simply visit our homepage and click 'Try Demo' to experience our flashcards without creating an account. For full access, click 'Sign Up' to create an account and choose a plan."
      },
      {
        question: "Do I need to create an account to use RealEstart?",
        answer: "No! You can try our demo with 10 practice questions without creating an account. However, to access all 500 flashcards and 152 practice test questions, you'll need to create a free account."
      },
      {
        question: "What's included in the free trial?",
        answer: "Our 3-day free trial gives you full access to all features including 500 flashcards, 152 practice test questions, detailed explanations, progress tracking, and study analytics. No credit card required!"
      }
    ],
    'account-billing': [
      {
        question: "How do I cancel my subscription?",
        answer: "You can cancel your subscription at any time through your account settings or by contacting our support team. Once cancelled, you'll retain access until the end of your current billing period."
      },
      {
        question: "Can I change my subscription plan?",
        answer: "Yes! You can upgrade or downgrade your plan at any time. Changes will take effect at the start of your next billing cycle."
      },
      {
        question: "Is there a refund policy?",
        answer: "All subscription fees are non-refundable. However, we reserve the right to offer refunds in exceptional circumstances at our sole discretion."
      }
    ],
    'technical-support': [
      {
        question: "The app isn't loading properly. What should I do?",
        answer: "First, try refreshing your browser. If the issue persists, clear your browser cache and cookies. For mobile users, try closing and reopening the app. If problems continue, contact our support team."
      },
      {
        question: "Can I use RealEstart on my mobile device?",
        answer: "Absolutely! RealEstart is fully responsive and works great on all devices including smartphones, tablets, and desktop computers. You can study anywhere, anytime!"
      },
      {
        question: "How do I reset my password?",
        answer: "On the login page, click 'Forgot Password' and enter your email address. We'll send you a secure link to reset your password."
      }
    ],
    'study-features': [
      {
        question: "How does the swipe interface work?",
        answer: "Swipe right on flashcards you know well, and left on those you need to review. Our system adapts to your learning pace and focuses on areas where you need more practice."
      },
      {
        question: "Can I bookmark questions for later review?",
        answer: "Yes! Click the bookmark icon on any flashcard to save it for later. You can access all your bookmarked questions from your dashboard."
      },
      {
        question: "How do I track my progress?",
        answer: "Your dashboard shows comprehensive statistics including accuracy, streak, and overall progress. You can also see which topics you've mastered and which need more attention."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Help Center</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions and learn how to make the most of RealEstart. Can't find what you're looking for? Contact our support team.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder="Search for help articles..."
              className="w-full px-6 py-4 pl-12 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-lg"
            />
            <svg className="w-6 h-6 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6">
          {Object.entries(faqs).map(([sectionKey, sectionFaqs]) => (
            <div key={sectionKey} className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <button
                onClick={() => toggleSection(sectionKey)}
                className="w-full px-8 py-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
              >
                <h2 className="text-2xl font-bold text-gray-900 capitalize">
                  {sectionKey.split('-').join(' ')}
                </h2>
                <svg 
                  className={`w-6 h-6 text-gray-500 transition-transform ${openSection === sectionKey ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {openSection === sectionKey && (
                <div className="px-8 pb-6 border-t border-gray-100">
                  <div className="space-y-6 pt-6">
                    {sectionFaqs.map((faq, index) => (
                      <div key={index} className="border-b border-gray-100 pb-6 last:border-b-0">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center flex flex-col h-full">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">Still Need Help?</h3>
            <p className="text-blue-100 mb-4 flex-grow">Can't find the answer you're looking for?</p>
            <Link 
              to="/contact" 
              className="inline-block bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Contact Support
            </Link>
          </div>

          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white text-center flex flex-col h-full">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">User Guide</h3>
            <p className="text-green-100 mb-4 flex-grow">Learn how to use all features effectively</p>
            <button className="inline-block bg-white text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Read Guide - Coming Soon
            </button>
          </div>

          <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-8 text-white text-center flex flex-col h-full">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">Video Tutorials</h3>
            <p className="text-orange-100 mb-4 flex-grow">Watch step-by-step video guides</p>
            <button className="inline-block bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Watch Videos - Coming Soon
            </button>
          </div>
        </div>

        {/* Back to Home */}
        <div className="mt-16 text-center">
          <Link 
            to="/" 
            className="bg-gray-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
